import { cn } from '@shadcn/lib/utils';
import { cva } from 'class-variance-authority';
import { useMemo } from 'react';

import { Header } from '../../atoms/header/header';
import { LayoutContext, useLayoutContext } from '../layout-context';

type PageWidth = 'narrow' | 'form' | 'wide' | 'full';
type PageAlign = 'top' | 'center';
type PageBackground = 'default' | 'muted';

type PageHeaderProps = {
  readonly breadcrumb?: React.ReactNode;
  readonly backLink?: React.ReactNode;
  readonly notice?: React.ReactNode;
  readonly title?: React.ReactNode;
  readonly actions?: React.ReactNode;
};

type PageLayoutProps = PageHeaderProps & {
  readonly width?: PageWidth;
  readonly align?: PageAlign;
  readonly background?: PageBackground;
  readonly bleed?: boolean;
  readonly className?: string;
  readonly children?: React.ReactNode;
};

const frameVariants = cva('flex w-full flex-1 flex-col', {
  variants: {
    background: { default: '', muted: 'bg-gray-50' },
    bleed: { false: 'px-4 py-6 sm:px-6 sm:py-8', true: '' },
    standalone: { false: '', true: 'min-h-dvh' },
  },
});

const columnVariants = cva('mx-auto flex w-full min-h-0 flex-1 flex-col', {
  variants: {
    align: { center: 'items-center justify-center gap-4 text-center', top: '' },
    width: { form: 'max-w-page-form', full: '', narrow: 'max-w-page-narrow', wide: 'max-w-page-wide' },
  },
});

const PageHeader = ({ breadcrumb, backLink, notice, title, actions }: PageHeaderProps) => (
  <>
    {breadcrumb}
    {backLink && <div className="mb-2">{backLink}</div>}
    {notice && <div className="mb-6">{notice}</div>}
    {(title || actions) && (
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        {title && <Header level="h1">{title}</Header>}
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    )}
  </>
);

function PageLayout({
  width = 'form',
  align = 'top',
  background = 'default',
  bleed = false,
  className,
  children,
  ...header
}: PageLayoutProps) {
  const parent = useLayoutContext();
  const value = useMemo(() => ({ ...parent, inPage: true }), [parent]);
  const column = (
    <div className={cn(columnVariants({ align, width }), parent.inPage && className)}>
      <PageHeader {...header} />
      {children}
    </div>
  );

  if (parent.inPage) return column;

  return (
    <LayoutContext.Provider value={value}>
      <div className={cn(frameVariants({ background, bleed, standalone: !parent.inShell }), className)}>{column}</div>
    </LayoutContext.Provider>
  );
}

PageLayout.displayName = 'PageLayout';

export type { PageAlign, PageBackground, PageLayoutProps, PageWidth };
export { PageLayout };
